import React, { useEffect, useMemo, useState } from "react";
import { Input, Stack, Textarea } from "@mui/joy";
import {
  COACHING_DOMAIN,
  CoachingFaq,
  coachingFaqApi,
  FAQ_LIST_ENDPOINT,
} from "api/coachingFaqApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import ButtonWithModal from "../../common/ButtonWithModal";
import Link from "../../common/Link";

interface EditCoachingFaqButtonProps {
  faq: CoachingFaq;
}

export default function EditCoachingFaqButton({
  faq,
}: EditCoachingFaqButtonProps) {
  const [question, setQuestion] = useState(faq.question);
  const [answer, setAnswer] = useState(faq.answer);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (faq) {
      setQuestion(faq.question);
      setAnswer(faq.answer);
    }
  }, [faq]);

  const updateMutation = useMutation({
    mutationFn: () => coachingFaqApi.update(faq.faqId, { question, answer }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COACHING_DOMAIN, FAQ_LIST_ENDPOINT],
      });
    },
  });

  const isUnchanged = useMemo(() => {
    return (
      question.trim() === faq.question.trim() &&
      answer.trim() === faq.answer.trim()
    );
  }, [question, answer, faq]);

  return (
    <>
      <ButtonWithModal
        buttonText=""
        title="FAQ 수정"
        content={
          <Stack spacing={2}>
            <Input
              placeholder="자주 묻는 질문을 적어주세요."
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              sx={{
                borderColor: "background.level3",
                borderWidth: "1px",
                "--joy-focus-thickness": "0px",
                boxShadow: "0px 0px 0px transparent",
              }}
            />
            <Textarea
              placeholder="답변을 적어주세요."
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              minRows={3}
              sx={{
                borderColor: "background.level3",
                borderWidth: "1px",
                "--joy-focus-thickness": "0px",
                boxShadow: "0px 0px 0px transparent",
              }}
            />
          </Stack>
        }
        actionText="수정하기"
        cancelText="취소"
        action={() => updateMutation.mutate()}
        actionButtonDisabled={
          updateMutation.isLoading ||
          isUnchanged ||
          question.trim().length === 0 ||
          answer.trim().length === 0
        }
        isWide={true}
        render={<Link text="수정" opacity={1} textColor="primary.solid" />}
      />
    </>
  );
}